import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels } from '@components/Table';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import ActionIconButton from '@components/ActionIconButton';
import { getChannelIcon } from '@helpers/channels';
import { LabelPlain } from '@components/Label';
import { useTranslation, useHasAccessToAsset } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { formatDateOrEmpty } from '@helpers/dates';
import { CAMPAIGN_STATUS } from '@constants/campaigns';
import { RIGHTS } from '@constants/user';
import { CHANNEL } from '@constants/channels';
import { campaignsActions } from '@actions';

const StyledTable = styled(Table)({
    '& .MuiTableBody-root .MuiTableCell-body:last-child': {
        padding: 4,
    },
});

const getTableHeader = t => {
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.channel')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('CampaignsPage.audience')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.scheduledTime')}
                </Typography>
            ),
        },
        {
            key: 5,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.status')}
                </Typography>
            ),
        },
        { key: 6, component: <Typography align="left" variant="subtitle2" /> },
    ];
};

const getTableBody = (data, language, channels, onClick, t, hasAccessToAssetById, isInboxAdmin) => {
    return data.map(campaign => {
        const rights = hasAccessToAssetById(campaign, 'campaigns');

        const isArchived = campaign.channelId === CHANNEL.ARCHIVED;
        const campaignChannelExists = channels?.some(c => c.id === campaign.channelId);
        const canWrite = rights[RIGHTS.WRITE] && (campaignChannelExists || isInboxAdmin);
        let channel;
        if (isArchived) {
            channel = { type: CHANNEL.ARCHIVED, name: t('common.archived') };
        } else if (!campaignChannelExists) {
            channel = { type: '', name: '' };
        } else {
            channel = channels.find(c => c.id === campaign.channelId);
        }
        return [
            {
                component: (
                    <Typography
                        variant="subtitle2"
                        align="left"
                        sx={{ width: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {campaign.title}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: 16 }}>{getChannelIcon(channel?.type)}</span> {channel?.name}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {campaign.labels?.length ? (
                            <Tooltip placement="top" title={campaign.labels.map(l => l.name).join(', ')}>
                                <span style={{ width: 120, overflow: 'hidden', display: 'flex' }}>
                                    {campaign.labels.map(label => (
                                        <LabelPlain key={label.id} type={label.name} variant="caption">
                                            {label.name}
                                        </LabelPlain>
                                    ))}
                                </span>
                            </Tooltip>
                        ) : (
                            '--'
                        )}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ width: 90 }}>
                        {formatDateOrEmpty(campaign.scheduledTime, 'dd MMM yyyy, HH:mm', language)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {t(campaign.isFailed ? 'common.failed' : `CampaignsPage.status${campaign.status}`)}
                        <span style={{ color: 'var(--text-secondary)' }}>
                            {' '}
                            ({campaign.progress}/{campaign.itemsCount})
                        </span>
                    </Typography>
                ),
            },
            {
                component: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <ActionIconButton title={t('common.view')} onClick={() => onClick(campaign, 'view')}>
                            <VisibilityIcon viewBox="0 0 22 22" />
                        </ActionIconButton>
                        {(campaign.status === CAMPAIGN_STATUS.SCHEDULED || campaign.isFailed) && canWrite && (
                            <ActionIconButton title={t('common.edit')} onClick={() => onClick(campaign, 'edit')}>
                                <EditIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                        )}
                        {(campaign.status === CAMPAIGN_STATUS.SCHEDULED ||
                            campaign.status === CAMPAIGN_STATUS.INPROGRESS ||
                            campaign.isFailed) &&
                            canWrite && (
                                <ActionIconButton
                                    title={t('common.delete')}
                                    onClick={() => onClick(campaign, 'delete')}
                                >
                                    <DeleteIcon viewBox="0 0 22 22" />
                                </ActionIconButton>
                            )}
                    </div>
                ),
            },
        ];
    });
};

const OneTimeTable = ({ rowsPerPage, page, setPage, setRowsPerPage, onClickCampaign }) => {
    const { t } = useTranslation();
    const { hasAccessToAssetById } = useHasAccessToAsset();
    const { isOwner, hasFullSettings } = usePermissions();
    const isInboxAdmin = isOwner || hasFullSettings();
    const { data, loading, totalCount, pages } = useSelector(state => state.campaigns);
    const { data: channels } = useSelector(state => state.channels);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(campaignsActions.get(newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(campaignsActions.get(0, parseInt(event.target.value, 10)));
    };

    return (
        <TableContainer>
            <StyledTable
                withHover
                columnNames={getTableHeader(t)}
                data={data}
                rows={getTableBody(data, language, channels, onClickCampaign, t, hasAccessToAssetById, isInboxAdmin)}
                onRowClick={campaign => onClickCampaign(campaign, 'view')}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 7,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default OneTimeTable;
